import { useEffect, useState } from 'react';
import { DeviceMobile, X, Share } from '@phosphor-icons/react';

/** Small floating banner that lets the user install the CRM as an app.
 * - Android/Chrome/Edge: uses the built-in `beforeinstallprompt` event.
 * - iOS Safari: shows a manual "Share → Add to Home Screen" instruction.
 * Once installed OR dismissed, we suppress it for 7 days via localStorage.
 */
const DISMISS_KEY = 'samudrika_pwa_install_dismissed_at';
const SUPPRESS_DAYS = 7;

function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
}
function isIOS() {
  const ua = window.navigator.userAgent || '';
  return /iphone|ipad|ipod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}
function recentlyDismissed() {
  const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!at) return false;
  return (Date.now() - at) < SUPPRESS_DAYS * 24 * 60 * 60 * 1000;
}

export default function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Chrome/Edge/Android
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS Safari has no beforeinstallprompt — show manual hint after a short delay
    if (isIOS()) {
      const t = setTimeout(() => setVisible(true), 4000);
      return () => { clearTimeout(t); window.removeEventListener('beforeinstallprompt', onBeforeInstall); };
    }
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch (_) { /* ignore */ }
      setDeferred(null);
      setVisible(false);
    } else if (isIOS()) {
      setShowIOSHint(true);
    }
  };
  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIOSHint(false);
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed left-3 right-3 md:left-auto md:right-6 md:bottom-6 bottom-3 z-[80] max-w-md md:w-96 bg-[#0A0A0A] text-white rounded-xl shadow-2xl border border-[#D4AF37]/40 p-4"
           data-testid="pwa-install-banner">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-[#D4AF37]/10">
            <DeviceMobile size={22} className="text-[#D4AF37]" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold">Install Samudrika CRM as an app</p>
            <p className="text-xs text-white/70 mt-0.5">Faster launch, full-screen, works even when your internet is spotty.</p>
            {showIOSHint && (
              <div className="mt-2 p-2 rounded bg-white/5 text-xs leading-relaxed">
                On iPhone Safari: tap <Share size={14} className="inline mx-0.5" /> <b>Share</b> → scroll → <b>Add to Home Screen</b>.
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <button onClick={install} className="px-3 py-1.5 rounded bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#B8942A]"
                      data-testid="pwa-install-btn">
                {deferred ? 'Install' : (isIOS() ? 'How?' : 'Install')}
              </button>
              <button onClick={dismiss} className="px-3 py-1.5 rounded border border-white/20 text-sm hover:bg-white/5"
                      data-testid="pwa-dismiss-btn">Not now</button>
            </div>
          </div>
          <button onClick={dismiss} className="text-white/50 hover:text-white" aria-label="dismiss"><X size={16} /></button>
        </div>
      </div>
    </>
  );
}
